// src/components/SmartRecolorTool.jsx
import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import { ChromePicker } from 'react-color';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../firebase';

// ============================================
// UTILITIES (moved from Editor.jsx)
// ============================================

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const imageCache = new Map();
const gradientCache = new Map();
const fullGradientCache = new Map();   // full‑resolution gradient maps

const getGradientMap = (imageElement, w, h, scale = 0.25) => {
  const sw = Math.max(1, Math.floor(w * scale));
  const sh = Math.max(1, Math.floor(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imageElement, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;
  const grad = new Float32Array(sw * sh);
  for (let y = 1; y < sh - 1; y++) {
    for (let x = 1; x < sw - 1; x++) {
      const idx = (y * sw + x) * 4;
      const gx =
        -1 * data[idx - 4] + -2 * data[idx - sw * 4 - 4] + -1 * data[idx + sw * 4 - 4] +
         1 * data[idx + 4] +  2 * data[idx + sw * 4 + 4] +  1 * data[idx - sw * 4 + 4] +
        (-1 * data[idx - sw * 4] + 1 * data[idx + sw * 4]) * 2;
      const gy =
        -1 * data[idx - sw * 4 - 4] + -2 * data[idx - sw * 4] + -1 * data[idx - sw * 4 + 4] +
         1 * data[idx + sw * 4 - 4] +  2 * data[idx + sw * 4] +  1 * data[idx + sw * 4 + 4] +
        (-1 * data[idx - 4] + 1 * data[idx + 4]) * 2;
      grad[y * sw + x] = Math.sqrt(gx * gx + gy * gy);
    }
  }
  return { grad, sw, sh };
};

const applyMagicBrushFast = (
  maskCanvas,
  brushX, brushY, brushRadius,
  mode,
  fullGradMap,        // gradient map at maskCanvas resolution
  edgeThreshold = 30
) => {
  const ctx = maskCanvas.getContext('2d');
  const w = maskCanvas.width;
  const h = maskCanvas.height;
  const { grad, sw, sh } = fullGradMap;   // sw == w, sh == h

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const cx = Math.round(brushX);
  const cy = Math.round(brushY);
  if (cx < 0 || cx >= w || cy < 0 || cy >= h) return;

  const targetValue = mode === 'add' ? 255 : 0;
  const currentOpposite = mode === 'add' ? 0 : 255;

  // Pre‑compute circle offsets to avoid Math inside the loop
  const r2 = brushRadius * brushRadius;
  const minX = Math.max(0, cx - brushRadius);
  const maxX = Math.min(w - 1, cx + brushRadius);
  const minY = Math.max(0, cy - brushRadius);
  const maxY = Math.min(h - 1, cy + brushRadius);

  for (let ny = minY; ny <= maxY; ny++) {
    const dy = ny - cy;
    const dy2 = dy * dy;
    for (let nx = minX; nx <= maxX; nx++) {
      const dx = nx - cx;
      if (dx * dx + dy2 > r2) continue;
      const idx = (ny * w + nx) * 4;
      if (data[idx] !== currentOpposite) continue;
      // Edge check – now direct lookup (no scaling)
      if (grad[ny * sw + nx] > edgeThreshold) continue;
      data[idx] = targetValue;
      data[idx + 1] = targetValue;
      data[idx + 2] = targetValue;
    }
  }
  ctx.putImageData(imageData, 0, 0);
};

const getLargestComponent = (maskData, w, h) => {
  const visited = new Uint8Array(w * h);
  let largestRegion = { pixels: [], count: 0 };

  const floodFill = (startIdx) => {
    const queue = [startIdx];
    const regionPixels = [];
    visited[startIdx] = 1;
    let count = 0;

    while (queue.length > 0) {
      const idx = queue.pop();
      regionPixels.push(idx);
      count++;

      const x = idx % w;
      const y = Math.floor(idx / w);

      const neighbors = [
        y > 0 ? idx - w : -1,
        y < h - 1 ? idx + w : -1,
        x > 0 ? idx - 1 : -1,
        x < w - 1 ? idx + 1 : -1,
      ];

      for (const nIdx of neighbors) {
        if (nIdx !== -1 && !visited[nIdx] && maskData[nIdx] === 255) {
          visited[nIdx] = 1;
          queue.push(nIdx);
        }
      }
    }

    return { pixels: regionPixels, count };
  };

  for (let i = 0; i < maskData.length; i++) {
    if (maskData[i] === 255 && !visited[i]) {
      const region = floodFill(i);
      if (region.count > largestRegion.count) {
        largestRegion = region;
      }
    }
  }

  const result = new Uint8ClampedArray(w * h);
  for (const idx of largestRegion.pixels) {
    result[idx] = 255;
  }
  return result;
};

const applyGaussianBlurToMask = (maskData, w, h, radius) => {
  const kernel = [];
  const sigma = radius / 2;
  const twoSigma2 = 2 * sigma * sigma;
  let sum = 0;
  for (let x = -radius; x <= radius; x++) {
    const val = Math.exp(-(x * x) / twoSigma2);
    kernel.push(val);
    sum += val;
  }
  for (let i = 0; i < kernel.length; i++) kernel[i] /= sum;

  // Horizontal pass
  const temp = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let val = 0;
      for (let k = -radius; k <= radius; k++) {
        const nx = Math.min(w - 1, Math.max(0, x + k));
        val += maskData[y * w + nx] * kernel[k + radius];
      }
      temp[y * w + x] = val;
    }
  }

  // Vertical pass
  const result = new Uint8ClampedArray(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let val = 0;
      for (let k = -radius; k <= radius; k++) {
        const ny = Math.min(h - 1, Math.max(0, y + k));
        val += temp[ny * w + x] * kernel[k + radius];
      }
      result[y * w + x] = Math.min(255, Math.round(val));
    }
  }
  return result;
};

const traceContour = (rgbaData, w, h) => {
  // Find first white pixel (alpha > 128)
  let startX = -1, startY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4 + 3;
      if (rgbaData[idx] > 128) {
        startX = x; startY = y;
        break;
      }
    }
    if (startX !== -1) break;
  }
  if (startX === -1) return null;

  const dirX = [ -1, 0, 1, 1, 1, 0, -1, -1 ];
  const dirY = [ 0, -1, -1, 0, 1, 1, 1, 0 ];
  const visited = new Uint8Array(w * h);
  const contour = [];

  let cx = startX, cy = startY;
  let startDir = 7;

  do {
    contour.push([cx, cy]);
    visited[cy * w + cx] = 1;

    let found = false;
    let searchDir = (startDir + 5) % 8;
    for (let i = 0; i < 8; i++) {
      const dir = (searchDir + i) % 8;
      const nx = cx + dirX[dir];
      const ny = cy + dirY[dir];
      if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
        const idx = (ny * w + nx) * 4 + 3;
        if (rgbaData[idx] > 128) {
          if (nx === startX && ny === startY && contour.length > 1) {
            contour.push([nx, ny]);
            return contour;
          }
          if (!visited[ny * w + nx]) {
            cx = nx; cy = ny;
            startDir = (dir + 4) % 8;
            found = true;
            break;
          }
        }
      }
    }
    if (!found) break;
  } while (contour.length < 10000);

  return contour;
};

const applyMorphologicalErosion = (maskData, w, h, radius) => {
  const result = new Uint8ClampedArray(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      if (maskData[idx] === 0) {
        result[idx] = 0;
        continue;
      }
      let allNeighborsInside = true;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny < 0 || ny >= h || nx < 0 || nx >= w) {
            allNeighborsInside = false;
            break;
          }
          if (maskData[ny * w + nx] === 0) {
            allNeighborsInside = false;
            break;
          }
        }
        if (!allNeighborsInside) break;
      }
      result[idx] = allNeighborsInside ? 255 : 0;
    }
  }
  return result;
};

// ============================================
// MAIN COMPONENT
// ============================================
const SmartRecolorTool = React.forwardRef(({ isOpen, onClose, canvasImage, onApplyWithMask, authUser, updateRemainingCredits, mainCanvasContainerRef, positionMode }, ref) => {
  const [selectedColor, setSelectedColor] = useState('#d4a574');
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('');
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [userMask, setUserMask] = useState(null);
  const [closedMaskPoints, setClosedMaskPoints] = useState(null);
  const [livePointerPos, setLivePointerPos] = useState(null);
  const [position, setPosition] = useState({ x: window.innerWidth - 340, y: 100 });
  const [previewImage, setPreviewImage] = useState(null);
  const [refinedMask, setRefinedMask] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  const [subtractBrushOn, setSubtractBrushOn] = useState(false);
  const [brushMode, setBrushMode] = useState('subtract');
  const [isBrushing, setIsBrushing] = useState(false);
  const [magicBrushEnabled, setMagicBrushEnabled] = useState(true);
  const [brushSize, setBrushSize] = useState(20);
  const [advancedDetection, setAdvancedDetection] = useState(false);
  const stripePatternRef = useRef(null);
  const [imgWidth, setImgWidth] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);

  const colorPickerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const tempMaskCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const liveMaskCanvasRef = useRef(null);
  const brushCursorCanvasRef = useRef(null);
  const canvasRectRef = useRef(null);
  const brushCursorRafRef = useRef(null);
  const brushUpdateTimeoutRef = useRef(null);
  const lastBrushPosRef = useRef({ x: 0, y: 0 });

  // Load original image dimensions
  useEffect(() => {
    if (canvasImage) {
      const img = new Image();
      img.onload = () => {
        setImgWidth(img.width);
        setImgHeight(img.height);
      };
      img.src = canvasImage;
    }
  }, [canvasImage]);

  // Pre‑load gradient maps
  useEffect(() => {
    if (!magicBrushEnabled || !canvasImage) return;
    if (gradientCache.has(canvasImage) && fullGradientCache.has(canvasImage)) return;

    const loadGradient = async () => {
      try {
        const img = imageCache.get(canvasImage) || await loadImageElement(canvasImage);
        if (!img) return;
        // existing 0.25 scale gradient
        if (!gradientCache.has(canvasImage)) {
          const gm = getGradientMap(img, img.width, img.height, 0.25);
          gradientCache.set(canvasImage, gm);
        }
        // full‑resolution gradient (for the mask canvas)
        if (!fullGradientCache.has(canvasImage)) {
          const fullGm = getGradientMap(img, img.width, img.height, 1.0);
          fullGradientCache.set(canvasImage, fullGm);
        }
      } catch (err) {
        console.warn('Failed to compute gradient map:', err);
      }
    };
    loadGradient();
  }, [magicBrushEnabled, canvasImage]);

  // Update live mask canvas whenever refinedMask changes
  useEffect(() => {
    if (refinedMask) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        liveMaskCanvasRef.current = canvas;
      };
      img.src = refinedMask;
    } else {
      liveMaskCanvasRef.current = null;
    }
  }, [refinedMask]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setPolygonPoints([]);
      setLivePointerPos(null);
      setUserMask(null);
      setRefinedMask(null);
      setPreviewImage(null);
      setStatus('Click to add polygon points. Double-click or click near the first point to close.');
      setSelectedColor('#d4a574');
      setClosedMaskPoints(null);

      let attempts = 0;
      const MAX_ATTEMPTS = 20;
      const tryCreateCanvas = () => {
        if (tempMaskCanvasRef.current && previewCanvasRef.current && brushCursorCanvasRef.current) return;
        if (mainCanvasContainerRef?.current) {
          if (!tempMaskCanvasRef.current) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.style.position = 'absolute';
            tempCanvas.style.top = '0';
            tempCanvas.style.left = '0';
            tempCanvas.style.width = '100%';
            tempCanvas.style.height = '100%';
            tempCanvas.style.pointerEvents = 'none';
            tempCanvas.style.zIndex = '15';
            tempMaskCanvasRef.current = tempCanvas;
            mainCanvasContainerRef.current.appendChild(tempCanvas);
          }
          if (!previewCanvasRef.current) {
            const prevCanvas = document.createElement('canvas');
            prevCanvas.style.position = 'absolute';
            prevCanvas.style.top = '0';
            prevCanvas.style.left = '0';
            prevCanvas.style.width = '100%';
            prevCanvas.style.height = '100%';
            prevCanvas.style.pointerEvents = 'none';
            prevCanvas.style.zIndex = '16';
            previewCanvasRef.current = prevCanvas;
            mainCanvasContainerRef.current.appendChild(prevCanvas);
          }
          if (!brushCursorCanvasRef.current) {
            const cursorCanvas = document.createElement('canvas');
            cursorCanvas.style.position = 'absolute';
            cursorCanvas.style.top = '0';
            cursorCanvas.style.left = '0';
            cursorCanvas.style.width = '100%';
            cursorCanvas.style.height = '100%';
            cursorCanvas.style.pointerEvents = 'none';
            cursorCanvas.style.zIndex = '17';
            brushCursorCanvasRef.current = cursorCanvas;
            mainCanvasContainerRef.current.appendChild(cursorCanvas);
          }
        } else if (attempts < MAX_ATTEMPTS) {
          attempts++;
          setTimeout(tryCreateCanvas, 100);
        }
      };
      tryCreateCanvas();
    } else {
      if (tempMaskCanvasRef.current) {
        tempMaskCanvasRef.current.remove();
        tempMaskCanvasRef.current = null;
      }
      if (previewCanvasRef.current) {
        previewCanvasRef.current.remove();
        previewCanvasRef.current = null;
      }
      if (brushCursorCanvasRef.current) {
        brushCursorCanvasRef.current.remove();
        brushCursorCanvasRef.current = null;
      }
      stripePatternRef.current = null;
    }
  }, [isOpen, mainCanvasContainerRef]);

  // Redraw closed mask points border
  useEffect(() => {
    if (isOpen && closedMaskPoints) {
      drawTempMask(closedMaskPoints, true);
    }
  }, [isOpen, closedMaskPoints, selectedColor]);

  // Update temp canvas size on resize
  useEffect(() => {
    if (tempMaskCanvasRef.current && mainCanvasContainerRef?.current) {
      const rect = mainCanvasContainerRef.current.getBoundingClientRect();
      tempMaskCanvasRef.current.width = rect.width;
      tempMaskCanvasRef.current.height = rect.height;
    }
  }, [mainCanvasContainerRef]);

  // Draggable color picker logic
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingRef.current && colorPickerRef.current) {
        const newX = Math.max(10, Math.min(e.clientX - dragOffsetRef.current.x, window.innerWidth - 320));
        const newY = Math.max(10, Math.min(e.clientY - dragOffsetRef.current.y, window.innerHeight - 500));
        setPosition({ x: newX, y: newY });
      }
    };
    const handleMouseUp = () => { isDraggingRef.current = false; };
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.max(10, Math.min(prev.x, window.innerWidth - 320)),
        y: Math.max(10, Math.min(prev.y, window.innerHeight - 500))
      }));
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleColorPickerMouseDown = (e) => {
    if (e.target === colorPickerRef.current || colorPickerRef.current?.contains(e.target)) {
      isDraggingRef.current = true;
      const rect = colorPickerRef.current.getBoundingClientRect();
      dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
  };

  const drawTempMask = (points, isClosed = false, livePt = null) => {
    if (!tempMaskCanvasRef.current || !mainCanvasContainerRef?.current) return;
    const canvas = tempMaskCanvasRef.current;
    const container = mainCanvasContainerRef.current;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = rect.width;
    const height = rect.height;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (points.length === 0 && !livePt) return;

    // Helper to draw a point, clamped to container edge, with arrow if outside
    const drawClampedPoint = (x, y, fillStyle) => {
      let drawX = x, drawY = y;
      let onEdge = false;
      if (x < 0) { drawX = 0; onEdge = true; }
      if (x > width) { drawX = width; onEdge = true; }
      if (y < 0) { drawY = 0; onEdge = true; }
      if (y > height) { drawY = height; onEdge = true; }
      ctx.beginPath();
      ctx.arc(drawX, drawY, 4, 0, 2 * Math.PI);
      ctx.fillStyle = fillStyle;
      ctx.fill();
      if (onEdge) {
        ctx.beginPath();
        if (x < 0) {
          ctx.moveTo(drawX, drawY - 6);
          ctx.lineTo(drawX + 6, drawY);
          ctx.lineTo(drawX, drawY + 6);
        } else if (x > width) {
          ctx.moveTo(drawX, drawY - 6);
          ctx.lineTo(drawX - 6, drawY);
          ctx.lineTo(drawX, drawY + 6);
        } else if (y < 0) {
          ctx.moveTo(drawX - 6, drawY);
          ctx.lineTo(drawX, drawY + 6);
          ctx.lineTo(drawX + 6, drawY);
        } else if (y > height) {
          ctx.moveTo(drawX - 6, drawY);
          ctx.lineTo(drawX, drawY - 6);
          ctx.lineTo(drawX + 6, drawY);
        }
        ctx.fillStyle = '#ffaa00';
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(drawX, drawY, 1.5, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    };

    // Draw polygon lines
    if (points.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
      if (isClosed) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(69, 148, 249, 0.25)';
        ctx.fill();
      }
      ctx.strokeStyle = '#4594f9';
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.stroke();
    }

    // Draw all points (clamped)
    points.forEach(p => drawClampedPoint(p.x, p.y, '#4594f9'));

    // Draw live rubber-band line
    if (livePt && points.length > 0) {
      const last = points[points.length - 1];
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(livePt.x, livePt.y);
      ctx.strokeStyle = '#4594f9';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      drawClampedPoint(livePt.x, livePt.y, 'rgba(69, 148, 249, 0.5)');
    }
  };
  const createMaskFromPoints = async (points, canvasRect) => {
    if (!canvasImage || points.length < 3) return null;
    const img = await loadImageElement(canvasImage);
    const imgWidth = img.width;
    const imgHeight = img.height;

    // Compute the image draw area inside the container (same as preview drawing)
    const imgAspect = imgWidth / imgHeight;
    const containerAspect = canvasRect.width / canvasRect.height;
    let drawWidth, drawHeight, drawX, drawY;
    if (imgAspect > containerAspect) {
      drawWidth = canvasRect.width;
      drawHeight = canvasRect.width / imgAspect;
      drawX = 0;
      drawY = (canvasRect.height - drawHeight) / 2;
    } else {
      drawHeight = canvasRect.height;
      drawWidth = canvasRect.height * imgAspect;
      drawX = (canvasRect.width - drawWidth) / 2;
      drawY = 0;
    }

    // Map screen points to image coordinates, taking offset and scale into account
    const scaleX = imgWidth / drawWidth;
    const scaleY = imgHeight / drawHeight;
    const scaledPoints = points.map(p => ({
      x: Math.max(0, Math.min(imgWidth - 1, (p.x - drawX) * scaleX)),
      y: Math.max(0, Math.min(imgHeight - 1, (p.y - drawY) * scaleY))
    }));

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = imgWidth;
    maskCanvas.height = imgHeight;
    const ctx = maskCanvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, imgWidth, imgHeight);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);
    for (let i = 1; i < scaledPoints.length; i++) ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
    ctx.closePath();
    ctx.fill();
    return maskCanvas;
  };

  const closePolygon = async (points, rect) => {
    if (points.length < 3) {
      setStatus('Please draw at least 3 points.');
      return;
    }
    const maskCanvas = await createMaskFromPoints(points, rect);
    if (!maskCanvas) return;
    const maskUrl = maskCanvas.toDataURL();
    canvasRectRef.current = rect;
    setUserMask(maskUrl);
    setRefinedMask(maskUrl);
    setClosedMaskPoints([...points]);
    if (tempMaskCanvasRef.current && mainCanvasContainerRef?.current) {
      const canvas = tempMaskCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const rect = mainCanvasContainerRef.current.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
    setPolygonPoints([]);
    setLivePointerPos(null);
    const refined = maskUrl;
    setRefinedMask(refined);
    setUserMask(refined);
    setStatus('Polygon ready! Pick a color.');
    applyRecolorToMask(refined, selectedColor);
  };

  const applyRecolorToMask = async (maskUrl, colorHex, overridePoints) => {
    if (!maskUrl || !canvasImage) return;
    setIsProcessing(true);
    setStatus('Applying color preview...');
    const img = await loadImageElement(canvasImage);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, img.width, img.height);
    const data = imageData.data;
    const maskImg = await loadImageElement(maskUrl);
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = img.width;
    maskCanvas.height = img.height;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.drawImage(maskImg, 0, 0);
    const maskData = maskCtx.getImageData(0, 0, img.width, img.height);
    const colorRgb = hexToRgb(colorHex);
    for (let i = 0; i < maskData.data.length; i += 4) {
      const maskVal = maskData.data[i] / 255;
      if (maskVal > 0.01) {
        const r = data[i];
        const g = data[i+1];
        const b = data[i+2];
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        const newR = Math.min(255, colorRgb.r * (luminance / 200));
        const newG = Math.min(255, colorRgb.g * (luminance / 200));
        const newB = Math.min(255, colorRgb.b * (luminance / 200));
        data[i]   = r * (1 - maskVal) + newR * maskVal;
        data[i+1] = g * (1 - maskVal) + newG * maskVal;
        data[i+2] = b * (1 - maskVal) + newB * maskVal;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    setPreviewImage(canvas.toDataURL('image/png'));

    const drawPoints = overridePoints || closedMaskPoints;
    if (previewCanvasRef.current && mainCanvasContainerRef?.current && drawPoints) {
      const prevCanvas = previewCanvasRef.current;
      const pCtx = prevCanvas.getContext('2d');
      const container = mainCanvasContainerRef.current;
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      prevCanvas.width = rect.width * dpr;
      prevCanvas.height = rect.height * dpr;
      prevCanvas.style.width = rect.width + 'px';
      prevCanvas.style.height = rect.height + 'px';
      pCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      pCtx.clearRect(0, 0, rect.width, rect.height);

      const imgAspect = img.width / img.height;
      const containerAspect = rect.width / rect.height;
      let drawWidth, drawHeight, drawX, drawY;
      if (imgAspect > containerAspect) {
        drawWidth = rect.width;
        drawHeight = rect.width / imgAspect;
        drawX = 0;
        drawY = (rect.height - drawHeight) / 2;
      } else {
        drawHeight = rect.height;
        drawWidth = rect.height * imgAspect;
        drawX = (rect.width - drawWidth) / 2;
        drawY = 0;
      }

      pCtx.save();
      pCtx.beginPath();
      pCtx.moveTo(drawPoints[0].x, drawPoints[0].y);
      for (let i = 1; i < drawPoints.length; i++) {
        pCtx.lineTo(drawPoints[i].x, drawPoints[i].y);
      }
      pCtx.closePath();
      pCtx.clip();

      pCtx.drawImage(canvas, drawX, drawY, drawWidth, drawHeight);
      pCtx.restore();

      pCtx.beginPath();
      pCtx.moveTo(drawPoints[0].x, drawPoints[0].y);
      for (let i = 1; i < drawPoints.length; i++) {
        pCtx.lineTo(drawPoints[i].x, drawPoints[i].y);
      }
      pCtx.closePath();
      pCtx.strokeStyle = '#4594f9';
      pCtx.lineWidth = 3;
      pCtx.setLineDash([]);
      pCtx.stroke();

      drawPoints.forEach(p => {
        pCtx.beginPath();
        pCtx.arc(p.x, p.y, 4, 0, 2 * Math.PI);
        pCtx.fillStyle = '#4594f9';
        pCtx.fill();
        pCtx.beginPath();
        pCtx.arc(p.x, p.y, 1.5, 0, 2 * Math.PI);
        pCtx.fillStyle = '#ffffff';
        pCtx.fill();
      });
    }

    setStatus('Color preview ready. Click "Apply to Canvas" to save.');
    setIsProcessing(false);
  };

  const updateMaskFromLiveCanvas = useCallback((newCanvas) => {
    if (!newCanvas) return;
    const url = newCanvas.toDataURL('image/png');
    setRefinedMask(url);
    setUserMask(url);
    stripePatternRef.current = null;
    applyRecolorToMask(url, selectedColor);
  }, [selectedColor]);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 212, g: 165, b: 116 };
  };

  const handleApplyToCanvas = async () => {
    if (!onApplyWithMask || !refinedMask || !canvasImage) return;
    setIsProcessing(true);
    setStatus('Applying to canvas...');
    try {
      // 1. Clear the preview overlay so the polygon border disappears
      if (previewCanvasRef.current) {
        const pCanvas = previewCanvasRef.current;
        const pCtx = pCanvas.getContext('2d');
        pCtx.clearRect(0, 0, pCanvas.width, pCanvas.height);
      }
      if (tempMaskCanvasRef.current) {
        const tCanvas = tempMaskCanvasRef.current;
        const tCtx = tCanvas.getContext('2d');
        tCtx.clearRect(0, 0, tCanvas.width, tCanvas.height);
      }

      // 2. Build a flat composite: original image + recoloured wall
      const img = await loadImageElement(canvasImage);
      const maskImg = await loadImageElement(refinedMask);
      const w = img.width;
      const h = img.height;

      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = w;
      compositeCanvas.height = h;
      const ctx = compositeCanvas.getContext('2d');

      // Draw original image first
      ctx.drawImage(img, 0, 0, w, h);
      const origImageData = ctx.getImageData(0, 0, w, h);
      const origData = origImageData.data;

      // Get mask data
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = w;
      maskCanvas.height = h;
      const maskCtx = maskCanvas.getContext('2d');
      maskCtx.drawImage(maskImg, 0, 0, w, h);
      const maskImageData = maskCtx.getImageData(0, 0, w, h);
      const maskData = maskImageData.data;

      const colorRgb = hexToRgb(selectedColor);

      for (let i = 0; i < maskData.length; i += 4) {
        const maskVal = maskData[i] / 255;
        if (maskVal > 0.01) {
          const r = origData[i];
          const g = origData[i + 1];
          const b = origData[i + 2];
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          origData[i]     = Math.min(255, colorRgb.r * (luminance / 200));
          origData[i + 1] = Math.min(255, colorRgb.g * (luminance / 200));
          origData[i + 2] = Math.min(255, colorRgb.b * (luminance / 200));
          // alpha stays 255 (original is opaque)
        }
      }
      ctx.putImageData(origImageData, 0, 0);

      const finalUrl = compositeCanvas.toDataURL('image/png');
      onApplyWithMask(finalUrl, closedMaskPoints);
      onClose();
    } catch (err) {
      console.error('Apply failed:', err);
      setStatus('Failed to apply.');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleReset = () => {
    setPolygonPoints([]);
    setLivePointerPos(null);
    setUserMask(null);
    setRefinedMask(null);
    setClosedMaskPoints(null);
    setPreviewImage(null);
    setSubtractBrushOn(false);
    setBrushMode('subtract');
    stopCursorAnimation();
    setStatus('Click to add polygon points. Double-click or click near the first point to close.');
    if (tempMaskCanvasRef.current) {
      const canvas = tempMaskCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
    }
    if (previewCanvasRef.current) {
      const pCanvas = previewCanvasRef.current;
      const pCtx = pCanvas.getContext('2d');
      pCtx.clearRect(0, 0, pCanvas.width / (window.devicePixelRatio || 1), pCanvas.height / (window.devicePixelRatio || 1));
    }
    stripePatternRef.current = null;
    liveMaskCanvasRef.current = null;
  };

  const startDrawing = (e, rect) => {
    if (!isOpen) return;

    if (subtractBrushOn && userMask && refinedMask && previewCanvasRef.current) {
      setIsBrushing(true);
      return;
    }

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (polygonPoints.length >= 3) {
      const first = polygonPoints[0];
      const dist = Math.hypot(x - first.x, y - first.y);
      if (dist < 12) {
        closePolygon(polygonPoints, rect);
        return;
      }
    }
    setPolygonPoints(prev => [...prev, { x, y }]);
    drawTempMask([...polygonPoints, { x, y }], false, livePointerPos);
  };

  const drawBrushCursor = (x, y) => {
    if (!brushCursorCanvasRef.current || !mainCanvasContainerRef?.current) return;
    const cursorCanvas = brushCursorCanvasRef.current;
    const container = mainCanvasContainerRef.current;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (cursorCanvas.width !== rect.width * dpr || cursorCanvas.height !== rect.height * dpr) {
      cursorCanvas.width = rect.width * dpr;
      cursorCanvas.height = rect.height * dpr;
      cursorCanvas.style.width = rect.width + 'px';
      cursorCanvas.style.height = rect.height + 'px';
    }
    const ctx = cursorCanvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, 2 * Math.PI);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const clearBrushCursor = () => {
    if (brushCursorCanvasRef.current) {
      const ctx = brushCursorCanvasRef.current.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, brushCursorCanvasRef.current.width / dpr, brushCursorCanvasRef.current.height / dpr);
    }
  };

  const startCursorAnimation = () => {
    if (brushCursorRafRef.current) return;
    const animate = () => {
      drawBrushCursor(lastBrushPosRef.current.x, lastBrushPosRef.current.y);
      brushCursorRafRef.current = requestAnimationFrame(animate);
    };
    brushCursorRafRef.current = requestAnimationFrame(animate);
  };

  const stopCursorAnimation = () => {
    if (brushCursorRafRef.current) {
      cancelAnimationFrame(brushCursorRafRef.current);
      brushCursorRafRef.current = null;
    }
    clearBrushCursor();
  };

  const draw = (e, rect) => {
    if (!isOpen) return;

    // --- BRUSHING MODE ---
    if (subtractBrushOn) {
      let clientX, clientY;
      if (e.touches) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      lastBrushPosRef.current = { x, y };

      // Always show cursor (animated)
      if (!brushCursorRafRef.current) startCursorAnimation();

      if (isBrushing && userMask && refinedMask && liveMaskCanvasRef.current) {
        const maskCanvas = liveMaskCanvasRef.current;
        const scaleX = maskCanvas.width / rect.width;
        const scaleY = maskCanvas.height / rect.height;
        const brushX = x * scaleX;
        const brushY = y * scaleY;
        const brushRadius = brushSize;

        // Fast painting on mask
        if (magicBrushEnabled) {
          // ensure full‑res gradient is ready
          if (!fullGradientCache.has(canvasImage)) {
            // fall back to non‑magic if not ready
            const ctx = maskCanvas.getContext('2d');
            ctx.fillStyle = brushMode === 'add' ? '#ffffff' : '#000000';
            ctx.beginPath();
            ctx.arc(brushX, brushY, brushRadius, 0, 2 * Math.PI);
            ctx.fill();
          } else {
            applyMagicBrushFast(
              maskCanvas,
              brushX, brushY, brushRadius,
              brushMode,
              fullGradientCache.get(canvasImage),
              30
            );
          }
        } else {
          const ctx = maskCanvas.getContext('2d');
          ctx.fillStyle = brushMode === 'add' ? '#ffffff' : '#000000';
          ctx.beginPath();
          ctx.arc(brushX, brushY, brushRadius, 0, 2 * Math.PI);
          ctx.fill();
        }

        // Debounced preview update (150ms after last stroke)
        if (brushUpdateTimeoutRef.current) {
          clearTimeout(brushUpdateTimeoutRef.current);
        }
        brushUpdateTimeoutRef.current = setTimeout(() => {
          brushUpdateTimeoutRef.current = null;
          updateMaskFromLiveCanvas(maskCanvas);
        }, 150);
      }
      return;
    }

    // --- POLYGON DRAWING MODE ---
    stopCursorAnimation();
    if (polygonPoints.length === 0) return;
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setLivePointerPos({ x, y });
    drawTempMask(polygonPoints, false, { x, y });
  };

  const stopDrawing = async (e, rect) => {
    if (!isOpen) return;
    if (subtractBrushOn && isBrushing) {
      setIsBrushing(false);
      if (brushUpdateTimeoutRef.current) {
        clearTimeout(brushUpdateTimeoutRef.current);
        brushUpdateTimeoutRef.current = null;
      }
      if (liveMaskCanvasRef.current) {
        updateMaskFromLiveCanvas(liveMaskCanvasRef.current);
      }
      stopCursorAnimation();
      return;
    }
    if (polygonPoints.length < 3) return;
    if (e.detail === 2 || (e.type === 'touchend' && e.touches?.length === 0)) {
      closePolygon(polygonPoints, rect);
    }
  };

  useImperativeHandle(ref, () => ({
    startDrawing,
    draw,
    stopDrawing,
  }), [startDrawing, draw, stopDrawing]);

  // Auto-detect wall using Grounded SAM
  const autoDetectWall = async () => {
    if (!canvasImage || !mainCanvasContainerRef?.current || !closedMaskPoints || closedMaskPoints.length < 3 || !imgWidth || !imgHeight) {
      setStatus('Please draw a rough polygon around the wall area first.');
      return;
    }
    setIsAutoDetecting(true);
    setStatus('Uploading region for wall segmentation...');
    try {
      const container = mainCanvasContainerRef.current;
      const rect = container.getBoundingClientRect();
      
      // Account for letterboxing (image centred inside container)
      const imgAspect0 = imgWidth / imgHeight;
      const containerAspect0 = rect.width / rect.height;
      let drawW0, drawH0, drawX0, drawY0;
      if (imgAspect0 > containerAspect0) {
        drawW0 = rect.width;
        drawH0 = rect.width / imgAspect0;
        drawX0 = 0;
        drawY0 = (rect.height - drawH0) / 2;
      } else {
        drawH0 = rect.height;
        drawW0 = rect.height * imgAspect0;
        drawX0 = (rect.width - drawW0) / 2;
        drawY0 = 0;
      }
      const scaleX = imgWidth / drawW0;
      const scaleY = imgHeight / drawH0;
      const imgPoints = closedMaskPoints.map(p => ({
        x: (p.x - drawX0) * scaleX,
        y: (p.y - drawY0) * scaleY
      }));

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      imgPoints.forEach(p => { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); });
      const padding = 40;
      minX = Math.max(0, minX - padding); minY = Math.max(0, minY - padding);
      maxX = Math.min(imgWidth, maxX + padding); maxY = Math.min(imgHeight, maxY + padding);

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = maxX - minX;
      cropCanvas.height = maxY - minY;
      const cropCtx = cropCanvas.getContext('2d');
      const img = await loadImageElement(canvasImage);
      cropCtx.drawImage(img, minX, minY, cropCanvas.width, cropCanvas.height, 0, 0, cropCanvas.width, cropCanvas.height);

      const cropBlob = await new Promise(resolve => cropCanvas.toBlob(resolve, 'image/jpeg', 0.9));
      const storage = getStorage();
      const uid = authUser?.uid || 'anon';
      const timestamp = Date.now();
      const cropRef = storageRef(storage, `temp_detections/${uid}/${timestamp}_crop.jpg`);
      await uploadBytes(cropRef, cropBlob);
      const cropUrl = await getDownloadURL(cropRef);

      setStatus('Segmenting wall with Grounded SAM...');
      const samRes = await axios.post(
        (import.meta.env.DEV ? '/api/replicate' : 'https://api.replicate.com') + '/v1/predictions',
        {
          version: "schananas/grounded_sam:ee871c19efb1941f55f66a3d7d960428c8a5afcb77449547fe8e5a3ab9ebc21c",
          input: {
            image: cropUrl,
            mask_prompt: "wall",
            negative_mask_prompt: "furniture, chair, table, sofa, bed, lamp, plant, cabinet, shelf, rug, painting, cushion, pillow, books, decoration, mirror, art, frame, clock, curtain",
            adjustment_factor: 5
          }
        },
        { headers: { 'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}` } }
      );

      const predId = samRes.data.id;
      let result = null;
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const pollUrl = (import.meta.env.DEV ? '/api/replicate' : 'https://api.replicate.com') + `/v1/predictions/${predId}`;
        const poll = await axios.get(pollUrl, {
          headers: { 'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}` }
        });
        if (poll.data.status === 'succeeded') {
          result = poll.data.output;
          break;
        } else if (poll.data.status === 'failed') {
          throw new Error('Grounded SAM failed');
        }
      }
      if (!result) throw new Error('Segmentation timed out');

      let maskUrl = null;
      if (Array.isArray(result)) {
        const first = result[0];
        if (typeof first === 'string') maskUrl = first;
        else if (first?.url) maskUrl = first.url;
      } else if (typeof result === 'string') maskUrl = result;
      else if (result?.url) maskUrl = result.url;
      if (!maskUrl) throw new Error('No mask URL found');

      const maskImg = await loadImageElement(maskUrl);
      const maskCanvasElem = document.createElement('canvas');
      maskCanvasElem.width = maskImg.width;
      maskCanvasElem.height = maskImg.height;
      const maskCtx = maskCanvasElem.getContext('2d');
      maskCtx.drawImage(maskImg, 0, 0);
      const maskData = maskCtx.getImageData(0, 0, maskCanvasElem.width, maskCanvasElem.height).data;

      const polyMaskInCrop = document.createElement('canvas');
      polyMaskInCrop.width = cropCanvas.width;
      polyMaskInCrop.height = cropCanvas.height;
      const polyCropCtx = polyMaskInCrop.getContext('2d');
      polyCropCtx.fillStyle = '#000';
      polyCropCtx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);
      polyCropCtx.fillStyle = '#fff';
      polyCropCtx.beginPath();
      polyCropCtx.moveTo(imgPoints[0].x - minX, imgPoints[0].y - minY);
      for (let i = 1; i < imgPoints.length; i++) polyCropCtx.lineTo(imgPoints[i].x - minX, imgPoints[i].y - minY);
      polyCropCtx.closePath();
      polyCropCtx.fill();
      const polyCropData = polyCropCtx.getImageData(0, 0, cropCanvas.width, cropCanvas.height).data;

      let wallMaskData = new Uint8ClampedArray(cropCanvas.width * cropCanvas.height);
      for (let i = 0; i < wallMaskData.length; i++) {
        const samIsWall = maskData[i * 4] > 128;   // white = wall
        const inPoly = polyCropData[i * 4] > 128;
        wallMaskData[i] = (samIsWall && inPoly) ? 255 : 0;
      }

      wallMaskData = getLargestComponent(wallMaskData, cropCanvas.width, cropCanvas.height);

      // --- Advanced object exclusion ---
      if (advancedDetection) {
        setStatus('Excluding objects (mirrors, art, plants)...');
        try {
          const objRes = await axios.post(
            (import.meta.env.DEV ? '/api/replicate' : 'https://api.replicate.com') + '/v1/predictions',
            {
              version: "schananas/grounded_sam:ee871c19efb1941f55f66a3d7d960428c8a5afcb77449547fe8e5a3ab9ebc21c",
              input: {
                image: cropUrl,
                mask_prompt: "sofa, chair, furniture, table, cabinet, shelf, plant, leaves, painting, mirror, art, decoration, lamp, bed, cushion, pillow, books, rug, curtain, window, door, frame, clock, foreground, object",
                negative_mask_prompt: "wall, floor, ceiling",
                adjustment_factor: 0
              }
            },
            { headers: { 'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}` } }
          );
          const objPredId = objRes.data.id;
          let objResult = null;
          for (let i = 0; i < 10; i++) {
            await new Promise(r => setTimeout(r, 2000));
            const pollUrl = (import.meta.env.DEV ? '/api/replicate' : 'https://api.replicate.com') + `/v1/predictions/${objPredId}`;
            const poll = await axios.get(pollUrl, {
              headers: { 'Authorization': `Token ${import.meta.env.VITE_REPLICATE_API_TOKEN}` }
            });
            if (poll.data.status === 'succeeded') { objResult = poll.data.output; break; }
            if (poll.data.status === 'failed') throw new Error('Object detection failed');
          }
          if (objResult) {
            let objMaskUrl = null;
            if (Array.isArray(objResult)) {
              const first = objResult[0];
              objMaskUrl = typeof first === 'string' ? first : first?.url;
            } else if (typeof objResult === 'string') objMaskUrl = objResult;
            else if (objResult?.url) objMaskUrl = objResult.url;
            if (objMaskUrl) {
              const objMaskImg = await loadImageElement(objMaskUrl);
              const objMaskCanvas = document.createElement('canvas');
              objMaskCanvas.width = cropCanvas.width;
              objMaskCanvas.height = cropCanvas.height;
              const objCtx = objMaskCanvas.getContext('2d');
              objCtx.drawImage(objMaskImg, 0, 0, cropCanvas.width, cropCanvas.height);
              const objImgData = objCtx.getImageData(0, 0, cropCanvas.width, cropCanvas.height).data;
              const objBinary = new Uint8ClampedArray(cropCanvas.width * cropCanvas.height);
              for (let i = 0; i < objBinary.length; i++) {
                objBinary[i] = objImgData[i * 4] > 128 ? 255 : 0;
              }
              const erosionRadius = Math.max(1, Math.floor(Math.min(cropCanvas.width, cropCanvas.height) * 0.01));
              const erodedObj = applyMorphologicalErosion(objBinary, cropCanvas.width, cropCanvas.height, erosionRadius);
              // Count white pixels in eroded object mask
              let objPixelCount = 0;
              for (let i = 0; i < erodedObj.length; i++) {
                if (erodedObj[i] === 255) objPixelCount++;
              }
              const objectFraction = objPixelCount / (cropCanvas.width * cropCanvas.height);
              // Only subtract if object area is < 30% of crop (avoid wiping out the whole wall)
              if (objectFraction < 0.3) {
                for (let i = 0; i < wallMaskData.length; i++) {
                  if (erodedObj[i] === 255) wallMaskData[i] = 0;
                }
              }
            }
          }
        } catch (objErr) {
          console.warn('Object exclusion failed, keeping full wall mask:', objErr);
        }
        setStatus('Applying color preview...');
      }
      // --- end of advanced exclusion ---

      const blurRadius = Math.max(1, Math.floor(Math.min(cropCanvas.width, cropCanvas.height) * 0.01));
      wallMaskData = applyGaussianBlurToMask(wallMaskData, cropCanvas.width, cropCanvas.height, blurRadius);
      const erosionRadius = Math.max(1, Math.floor(Math.min(cropCanvas.width, cropCanvas.height) * 0.005));
      wallMaskData = applyMorphologicalErosion(wallMaskData, cropCanvas.width, cropCanvas.height, erosionRadius);

      const wallMaskCanvas = document.createElement('canvas');
      wallMaskCanvas.width = cropCanvas.width;
      wallMaskCanvas.height = cropCanvas.height;
      const wallCtx = wallMaskCanvas.getContext('2d');
      const wallImageData = wallCtx.createImageData(cropCanvas.width, cropCanvas.height);
      for (let i = 0; i < wallMaskData.length; i++) {
        const val = wallMaskData[i];
        wallImageData.data[i*4] = val;
        wallImageData.data[i*4+1] = val;
        wallImageData.data[i*4+2] = val;
        wallImageData.data[i*4+3] = 255;
      }
      wallCtx.putImageData(wallImageData, 0, 0);

      const contour = traceContour(wallImageData.data, cropCanvas.width, cropCanvas.height);
      let scaledPoints;

      if (!contour || contour.length < 20) {
        setStatus('Wall not clearly detected. Keeping original polygon.');
        scaledPoints = [...closedMaskPoints];
      } else {
        // Compute the draw area inside the container (same as createMaskFromPoints)
        const imgAspect2 = imgWidth / imgHeight;
        const containerAspect2 = rect.width / rect.height;
        let drawW, drawH, drawOffX, drawOffY;
        if (imgAspect2 > containerAspect2) {
          drawW = rect.width;
          drawH = rect.width / imgAspect2;
          drawOffX = 0;
          drawOffY = (rect.height - drawH) / 2;
        } else {
          drawH = rect.height;
          drawW = rect.height * imgAspect2;
          drawOffX = (rect.width - drawW) / 2;
          drawOffY = 0;
        }
        const scaleToScreenX = drawW / imgWidth;
        const scaleToScreenY = drawH / imgHeight;
        scaledPoints = contour.map(([cx, cy]) => ({
          x: drawOffX + (cx + minX) * scaleToScreenX,
          y: drawOffY + (cy + minY) * scaleToScreenY
        }));

        // Reject thin / line-like polygons
        let minX_s = Infinity, minY_s = Infinity, maxX_s = -Infinity, maxY_s = -Infinity;
        scaledPoints.forEach(p => {
          if (p.x < minX_s) minX_s = p.x;
          if (p.x > maxX_s) maxX_s = p.x;
          if (p.y < minY_s) minY_s = p.y;
          if (p.y > maxY_s) maxY_s = p.y;
        });
        const width_s = maxX_s - minX_s;
        const height_s = maxY_s - minY_s;
        const isLine = (width_s < 20 && height_s > 40) || (height_s < 20 && width_s > 40);
        if ((width_s < 20 && height_s < 20) || isLine) {
          setStatus('Wall not clearly detected. Keeping original polygon.');
          scaledPoints = [...closedMaskPoints];
        }
      }

      setClosedMaskPoints(scaledPoints);
      setPolygonPoints([]);
      setLivePointerPos(null);

      const fullMaskCanvas = document.createElement('canvas');
      fullMaskCanvas.width = imgWidth;
      fullMaskCanvas.height = imgHeight;
      const fullCtx = fullMaskCanvas.getContext('2d');
      fullCtx.drawImage(wallMaskCanvas, minX, minY);

      const polyFullCanvas = document.createElement('canvas');
      polyFullCanvas.width = imgWidth;
      polyFullCanvas.height = imgHeight;
      const polyFullCtx = polyFullCanvas.getContext('2d');
      polyFullCtx.fillStyle = '#000';
      polyFullCtx.fillRect(0, 0, imgWidth, imgHeight);
      polyFullCtx.fillStyle = '#fff';
      polyFullCtx.beginPath();
      polyFullCtx.moveTo(imgPoints[0].x, imgPoints[0].y);
      for (let i = 1; i < imgPoints.length; i++) polyFullCtx.lineTo(imgPoints[i].x, imgPoints[i].y);
      polyFullCtx.closePath();
      polyFullCtx.fill();
      const polyData = polyFullCtx.getImageData(0, 0, imgWidth, imgHeight).data;
      const aiData = fullCtx.getImageData(0, 0, imgWidth, imgHeight).data;
      const combinedData = fullCtx.createImageData(imgWidth, imgHeight);
      for (let i = 0; i < aiData.length; i += 4) {
        const aiWhite = aiData[i] > 128;
        const polyWhite = polyData[i] > 128;
        const val = (aiWhite && polyWhite) ? 255 : 0;
        combinedData.data[i] = val;
        combinedData.data[i+1] = val;
        combinedData.data[i+2] = val;
        combinedData.data[i+3] = 255;
      }
      fullCtx.putImageData(combinedData, 0, 0);
      const finalMaskUrl = fullMaskCanvas.toDataURL('image/png');
      setUserMask(finalMaskUrl);
      setRefinedMask(finalMaskUrl);
      setPreviewImage(null);
      setStatus('Wall detected. Adjust color or apply.');
      applyRecolorToMask(finalMaskUrl, selectedColor, scaledPoints);
    } catch (error) {
      console.error('Detect walls error:', error);
      setStatus('Detection failed: ' + error.message);
    } finally {
      setIsAutoDetecting(false);
    }
  };

  if (!isOpen) return null;

  const controlsContent = (
    <div 
      className={`recolor-controls ${positionMode === 'sidebar' ? 'recolor-sidebar' : ''}`}
      ref={positionMode !== 'sidebar' ? colorPickerRef : undefined}
      onMouseDown={positionMode !== 'sidebar' ? handleColorPickerMouseDown : undefined}
      style={positionMode === 'sidebar' ? {} : { 
        top: Math.max(10, Math.min(position.y, window.innerHeight - 500)),
        left: Math.max(10, Math.min(position.x, window.innerWidth - 320))
      }}
    >
      <div className="recolor-controls-header">
        <span className="recolor-drag-handle">{positionMode === 'sidebar' ? '' : '⋮⋮'}</span>
        <span className="recolor-title">Wall Color Tool</span>
        <button className="recolor-close-btn" onClick={onClose}>✕</button>
      </div>
      
      <div className="recolor-controls-content">
        {closedMaskPoints && closedMaskPoints.length > 0 ? (
          <>
            <div className="recolor-color-section">
              <label>Select Color:</label>
              <ChromePicker
                color={selectedColor}
                onChangeComplete={(color) => {
                  setSelectedColor(color.hex);
                  const maskToUse = refinedMask || userMask;
                  if (maskToUse) applyRecolorToMask(maskToUse, color.hex);
                }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <button
                className={`recolor-brush-btn ${subtractBrushOn ? 'active' : ''}`}
                onClick={() => {
                  setSubtractBrushOn(prev => {
                    if (prev) stopCursorAnimation();
                    return !prev;
                  });
                }}
              >
                {subtractBrushOn ? '✔ Brush On' : '🖌️ Brush Off'}
              </button>
              {subtractBrushOn && (
                <>
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button
                      className={`recolor-brush-sub-btn ${brushMode === 'subtract' ? 'active' : ''}`}
                      onClick={() => setBrushMode('subtract')}
                    >
                      ➖ Subtract
                    </button>
                    <button
                      className={`recolor-brush-sub-btn ${brushMode === 'add' ? 'active' : ''}`}
                      onClick={() => setBrushMode('add')}
                    >
                      ➕ Add
                    </button>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#ccc', fontSize: 11 }}>Size</span>
                    <input
                      type="range"
                      min="4"
                      max="60"
                      value={brushSize}
                      onChange={(e) => setBrushSize(Number(e.target.value))}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#ccc', fontSize: 11, minWidth: 20 }}>{brushSize}</span>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#ccc', fontSize: 11 }}>Magic</span>
                    <label className="magic-switch" style={{ position: 'relative', display: 'inline-block', width: 40, height: 20 }}>
                      <input
                        type="checkbox"
                        checked={magicBrushEnabled}
                        onChange={(e) => setMagicBrushEnabled(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span
                        className="magic-slider"
                        style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0, left: 0, right: 0, bottom: 0,
                          backgroundColor: magicBrushEnabled ? '#28a745' : '#ccc',
                          transition: '.3s',
                          borderRadius: 20,
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            height: 14,
                            width: 14,
                            left: magicBrushEnabled ? 22 : 3,
                            bottom: 3,
                            backgroundColor: 'white',
                            transition: '.3s',
                            borderRadius: '50%',
                          }}
                        />
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>
            {isRefining && <div className="recolor-status">Refining mask, please wait...</div>}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
              <span style={{ color: '#ccc', fontSize: 12 }}>Exclude objects</span>
              <label className="magic-switch" style={{ position: 'relative', display: 'inline-block', width: 36, height: 18 }}>
                <input
                  type="checkbox"
                  checked={advancedDetection}
                  onChange={(e) => setAdvancedDetection(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  className="magic-slider"
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: advancedDetection ? '#28a745' : '#ccc',
                    transition: '.3s',
                    borderRadius: 18,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      height: 12,
                      width: 12,
                      left: advancedDetection ? 20 : 2,
                      bottom: 3,
                      backgroundColor: 'white',
                      transition: '.3s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>
            <button
              className="recolor-auto-detect-btn"
              onClick={autoDetectWall}
              disabled={isAutoDetecting}
            >
              {isAutoDetecting ? 'Detecting...' : 'Detect Walls'}
            </button>
            {refinedMask && (
              <button
                className="recolor-invert-btn"
                onClick={() => {
                  if (!liveMaskCanvasRef.current) return;
                  const ctx = liveMaskCanvasRef.current.getContext('2d');
                  const w = liveMaskCanvasRef.current.width;
                  const h = liveMaskCanvasRef.current.height;
                  const imgData = ctx.getImageData(0, 0, w, h);
                  for (let i = 0; i < imgData.data.length; i += 4) {
                    const val = imgData.data[i];
                    const inv = val === 0 ? 255 : 0;
                    imgData.data[i] = inv;
                    imgData.data[i+1] = inv;
                    imgData.data[i+2] = inv;
                  }
                  ctx.putImageData(imgData, 0, 0);
                  updateMaskFromLiveCanvas(liveMaskCanvasRef.current);
                }}
              >
                Invert Mask
              </button>
            )}
            <div className="recolor-action-buttons">
              <button 
                className="recolor-apply-btn"
                onClick={handleApplyToCanvas}
                disabled={!previewImage || previewImage === canvasImage}
              >
                Apply to Canvas
              </button>
              <button 
                className="recolor-reset-btn"
                onClick={handleReset}
              >
                New Selection
              </button>
            </div>
          </>
        ) : (
          <div className="recolor-drawing-instructions">
            <p>✏️ <strong>Polygon Lasso:</strong></p>
            <ul>
              <li>Draw a rough polygon around the wall area</li>
              <li>Double‑click or tap near the first point to close</li>
              <li>Click <strong>Detect Walls</strong> to subtract furniture automatically</li>
              <li>Pick a color – preview updates live</li>
              <li>Click <strong>Apply to Canvas</strong> to save</li>
            </ul>
            {polygonPoints.length > 0 && (
              <button className="recolor-cancel-draw-btn" onClick={handleReset}>
                Cancel Drawing
              </button>
            )}
          </div>
        )}

        {status && <div className="recolor-status">{status}</div>}
      </div>
      <style>{`
        .recolor-controls {
          position: fixed;
          width: 300px;
          background: #3a3a3a;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          cursor: move;
          z-index: 2001;
          pointer-events: auto;
        }
        .theme-light .recolor-controls {
          background: #ffffff;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        }
        .recolor-controls-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #4594f9;
          border-radius: 12px 12px 0 0;
          cursor: move;
        }
        .recolor-drag-handle {
          color: white;
          font-size: 16px;
          cursor: grab;
          letter-spacing: 2px;
        }
        .recolor-drag-handle:active { cursor: grabbing; }
        .recolor-title {
          flex: 1;
          color: white;
          font-size: 14px;
          font-weight: 500;
          font-family: Avenir, sans-serif;
        }
        .recolor-close-btn {
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .recolor-close-btn:hover { background: rgba(255,255,255,0.2); }
        .recolor-controls-content { padding: 16px; }
        .recolor-color-section { margin-bottom: 20px; }
        .recolor-color-section label {
          display: block;
          color: #ccc;
          font-size: 12px;
          margin-bottom: 8px;
        }
        .theme-light .recolor-color-section label { color: #666; }
        .recolor-action-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .recolor-apply-btn {
          width: 100%;
          padding: 10px;
          border-radius: 25px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          font-family: Avenir, sans-serif;
          background: #28a745;
          color: white;
        }
        .recolor-reset-btn {
          width: 100%;
          padding: 10px;
          border-radius: 25px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          font-family: Avenir, sans-serif;
          background: #555;
          color: white;
        }
        .recolor-drawing-instructions {
          text-align: left;
          padding: 12px;
        }
        .recolor-drawing-instructions p {
          color: #ccc;
          font-size: 14px;
          margin-bottom: 8px;
        }
        .recolor-drawing-instructions ul {
          color: #aaa;
          font-size: 12px;
          padding-left: 20px;
          margin-bottom: 16px;
        }
        .recolor-auto-detect-btn {
          width: 100%;
          padding: 10px;
          border-radius: 20px;
          border: none;
          background: #3a3a5a;
          color: white;
          cursor: pointer;
          font-size: 13px;
          margin-top: 12px;
          transition: background 0.2s;
        }
        .recolor-auto-detect-btn:hover { background: #4a4a6a; }
        .recolor-auto-detect-btn:disabled { opacity: 0.5; cursor: wait; }
        .recolor-brush-btn {
          width: 100%;
          padding: 8px;
          border-radius: 20px;
          border: 1px solid #888;
          background: #3a3a3a;
          color: white;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s;
        }
        .recolor-invert-btn {
          width: 100%;
          padding: 8px;
          border-radius: 20px;
          border: 1px solid #888;
          background: #3a3a3a;
          color: white;
          cursor: pointer;
          font-size: 12px;
          margin-top: 6px;
          transition: all 0.2s;
        }
        .recolor-invert-btn:hover { background: #555; }
        .recolor-brush-btn.active {
          border-color: #4594f9;
          background: #2a4a6a;
        }
        .recolor-brush-btn:hover { background: #555; }
        .recolor-brush-sub-btn {
          flex: 1;
          padding: 6px 4px;
          border-radius: 12px;
          border: 1px solid #888;
          background: #3a3a3a;
          color: white;
          cursor: pointer;
          font-size: 11px;
          transition: all 0.2s;
        }
        .recolor-brush-sub-btn.active {
          border-color: #4594f9;
          background: #2a4a6a;
        }
        .recolor-brush-sub-btn:hover { background: #555; }
        .recolor-drawing-instructions li { margin: 6px 0; }
        .recolor-cancel-draw-btn {
          width: 100%;
          padding: 8px;
          border-radius: 20px;
          border: none;
          background: #e02f1d;
          color: white;
          cursor: pointer;
          font-size: 12px;
        }
        .recolor-status {
          text-align: center;
          margin-top: 12px;
          padding: 8px;
          font-size: 12px;
          color: #88b7ff;
          background: rgba(69,148,249,0.1);
          border-radius: 8px;
        }
        .recolor-sidebar {
          position: relative !important;
          width: 100% !important;
          height: 100% !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          pointer-events: auto;
          cursor: default;
          background: inherit;
        }
        .recolor-sidebar .recolor-controls-header {
          cursor: default;
          border-radius: 0;
        }
      `}</style>
    </div>
  );

  if (positionMode === 'sidebar') {
    return (
      <>
        {controlsContent}
        <style>{`
          .smart-recolor-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: transparent;
            z-index: 2000;
            pointer-events: none;
          }
        `}</style>
      </>
    );
  }

  return (
    <div className="smart-recolor-overlay">
      {controlsContent}
    </div>
  );
});

export default SmartRecolorTool;